import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { MenuTooltipTool } from '../../../../../../viewer/tools/tooltip-helper';
import { CoordinatePropertiesStyled } from '../../../tooltip/components/coordinate-properties';
import {
  AssetPositionProperties,
  hasTooltipContent,
  SkyviewAssetPositionProps,
} from '../../../tooltip/skyview-asset-position-tooltip';

type Props = {
  tool: MenuTooltipTool;
  light: boolean;
  /**
   * Shows the tooltip of the tool again, so that it moves between the cursor and the menu as soon
   * as it's placed there or removed. Otherwise it moves as the cursor is moved.
   */
  refresh?: () => void;
  /**
   * In the flow, as high as what it shows, instead of taking the same space and showing over what
   * is below it: a band across the bottom of a dialog, with nothing below it to cover. When the
   * cursor leaves the view, it keeps what it showed last, faded, so that it doesn't shrink as the
   * cursor reaches it: the cursor would be back in the view, and it would grow again, and flicker.
   */
  inFlow?: boolean;
  /** Above what it shows, in the flow. */
  title?: string;
  /**
   * Styles the space it takes, e.g. without the margins it has in the right-side menu.
   */
  className?: string;
};

/**
 * The tooltip of a tool, pinned at the top of a menu or a dialog instead of following the cursor,
 * e.g. of the design tool in the right-side menu.
 *
 * It takes the same space in the menu whatever it shows, so that the sections below don't move,
 * and is shown over them when it needs more. That is only while the cursor is in the view, since
 * it is cleared when the cursor leaves it. In the flow, it grows and stays instead, see inFlow.
 */
export const MenuTooltip = (props: Props) => {
  const { t } = useTranslation();
  const [shown, setShown] = React.useState<{
    tooltip?: SkyviewAssetPositionProps;
    /** Kept from before, the cursor having left the view since. */
    stale: boolean;
  }>({ stale: false });
  const refresh = React.useRef(props.refresh);
  refresh.current = props.refresh;
  const keepsLast = React.useRef(props.inFlow);
  keepsLast.current = props.inFlow;

  React.useEffect(() => {
    props.tool.onMenuTooltipChanged = (tooltip) =>
      setShown((current) =>
        tooltip && hasTooltipContent(tooltip)
          ? { tooltip, stale: false }
          : keepsLast.current && current.tooltip
            ? { tooltip: current.tooltip, stale: true }
            : { stale: false },
      );
    // Moves a tooltip shown next to the cursor into the menu, e.g. when the setting is turned on
    // with the keyboard while the cursor is in the view.
    refresh.current?.();

    return () => {
      props.tool.onMenuTooltipChanged = undefined;
      // And back next to the cursor. Nothing is shown when the tool is turned off, since the
      // cursor is then no longer in the view for it.
      refresh.current?.();
    };
  }, [props.tool]);

  return (
    <Slot $inFlow={!!props.inFlow} className={props.className}>
      <Component $inFlow={!!props.inFlow} data-testid="design-menu-tooltip" light={props.light}>
        {props.inFlow && props.title && <Title>{props.title}</Title>}
        {shown.tooltip ? (
          <Properties $stale={shown.stale} data-stale={shown.stale}>
            <AssetPositionProperties
              coordinate={shown.tooltip.coordinate}
              coordinateSystem={shown.tooltip.coordinateSystem}
              heights={shown.tooltip.heights}
              properties={shown.tooltip.properties}
            />
          </Properties>
        ) : (
          <Placeholder>{t('menuTooltip.placeholder', { ns: 'skyviewDesign' })}</Placeholder>
        )}
      </Component>
    </Slot>
  );
};

const Slot = styled.div<{ $inFlow: boolean }>`
  position: relative;
  // Over the sections below.
  z-index: 1;
  flex-shrink: 0;

  height: ${(props) => (props.$inFlow ? 'auto' : '4em')};
  margin: 0.8em 0.8em 0 0.8em;
  font-size: 10pt;
`;

const Placeholder = styled.span`
  color: #fffa;
`;

const Properties = styled.div<{ $stale: boolean }>`
  opacity: ${(props) => (props.$stale ? 0.5 : 1)};
  transition: opacity 0.15s;
`;

const Title = styled.div`
  margin-bottom: 0.6em;
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.5;
`;

const Component = styled.div<{ $inFlow: boolean; light: boolean }>`
  position: ${(props) => (props.$inFlow ? 'static' : 'absolute')};
  top: 0;
  right: 0;
  left: 0;
  min-height: 100%;
  box-sizing: border-box;

  padding: 0.5em;
  border-radius: 0.5em;

  // Opaque, unlike next to the cursor, since it is shown over the sections.
  background-color: rgb(64, 64, 64);
  color: #fff;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);

  font-family: 'Montserrat', sans-serif;

  // Next to the cursor the tooltip is as wide as it needs, here it fits the menu or the dialog: the
  // columns share the width, the labels wrap and nothing has a minimum width. Values don't wrap.
  && * {
    min-width: 0;
  }

  && ${CoordinatePropertiesStyled} {
    grid-auto-columns: minmax(0, 1fr);
    grid-column-gap: 1em;
    grid-row-gap: 0.5em;

    label {
      white-space: normal;
    }
  }

  // The content of the tooltip is made for a dark background.
  ${(props) =>
    props.light &&
    css`
      border: 1px solid ${props.theme.color.gray.light};
      background-color: #fff;
      color: ${props.theme.color.gray.darkest};

      && label,
      && ${Placeholder} {
        color: ${props.theme.color.gray.dark};
      }

      && input {
        background-color: ${props.theme.color.gray.lightest};
      }
    `}

  // A flat band, its sides the dialog's, set apart from the options above it by its line and a
  // lighter grey. Smaller, as the dialog's own text.
  ${(props) =>
    props.$inFlow &&
    css`
      padding: 0.9em 1.5em 1.1em;
      border: none;
      border-top: 1px solid ${props.theme.color.gray.light};
      border-radius: 0;
      box-shadow: none;
      font-size: 9.5pt;

      ${props.light &&
      css`
        background-color: #f7f8f8;
      `}

      && ${CoordinatePropertiesStyled} {
        grid-row-gap: 0.6em;

        label {
          margin-bottom: 0.1em;
        }
      }

      // The heights keep their width and the names get the ellipsis, the snapped one's mark at the end
      && [data-snapped] > span:first-of-type {
        flex-shrink: 0;
      }

      && [data-snapped='true'] > span:last-child {
        margin-left: auto;
      }
    `}
`;
